import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { fmtDate, relative } from "../lib/format";
import { downloadRfiLogPdf } from "../lib/pdf";
import { printTable } from "../lib/printView";
import type { RFI } from "../lib/types";

const BALL_LABEL: Record<string, string> = { studio: "Studio", client: "Client", consultant: "Consultant", contractor: "Contractor" };
const STATUS_HUE: Record<string, string> = { open: "var(--warn)", answered: "var(--sky)", closed: "var(--ink-3)" };

export default function Rfis() {
  const p = useCurrent();
  const canEdit = useCan("reviews");
  const session = useSession();
  const who = session?.name ?? STUDIO.principal;
  const [raise, setRaise] = useState(false);
  const [answerFor, setAnswerFor] = useState<RFI | null>(null);
  if (!p) return null;
  const rfis = [...(p.rfis ?? [])].sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  const open = rfis.filter((r) => r.status !== "closed").length;
  const ours = rfis.filter((r) => r.status !== "closed" && r.ballInCourt === "studio").length;

  return (
    <Page>
      <PageHead kicker={`${p.name} · ${p.code}`} title="RFIs" />
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 18 }}>
        Requests for Information raised during tender and construction — routed by who they're waiting on, tracked, answered, and compiled into a log you can issue.
      </p>
      <div className="row wrap reveal" style={{ gap: 8, marginBottom: 20, alignItems: "center" }}>
        <span className="badge"><Icon.chat width={11} />{rfis.length} total</span>
        <span className="badge accent">{open} open</span>
        {ours > 0 && <span className="badge" style={{ color: "var(--warn)" }}>{ours} awaiting the studio</span>}
        <span style={{ flex: 1 }} />
        {rfis.length > 0 && <button className="btn ghost sm" onClick={() => printTable({
          title: "RFI log", subtitle: `${p.name} · ${p.code}`, meta: [`${rfis.length} RFIs`, `${open} open`],
          columns: [{ key: "ref", label: "Ref", width: "60px" }, { key: "subject", label: "Question" }, { key: "answer", label: "Answer" }, { key: "ball", label: "Ball", width: "60px" }, { key: "status", label: "Status", width: "60px" }],
          rows: rfis.map((r) => ({ ref: r.ref, subject: `${r.subject} — ${r.question}`, answer: r.answer ?? "Awaiting answer", ball: BALL_LABEL[r.ballInCourt], status: r.status })),
        })}><Icon.report width={13} />Print</button>}
        {rfis.length > 0 && <button className="btn ghost sm" onClick={() => downloadRfiLogPdf(p)}><Icon.download width={13} />RFI log (PDF)</button>}
        {canEdit && <button className="btn accent sm" onClick={() => setRaise(true)}><Icon.spark width={14} />Raise RFI</button>}
      </div>

      {rfis.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}>
          <div className="kicker">No RFIs yet</div>
          <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>Raise an RFI when the contractor or a consultant needs a clarification during the works. Each one is numbered and tracked to an answer.</p>
          {canEdit && <button className="btn accent" style={{ marginTop: 14 }} onClick={() => setRaise(true)}><Icon.spark width={15} />Raise the first RFI</button>}
        </div>
      ) : (
        <div className="col" style={{ gap: 12 }}>
          {rfis.map((r) => (
            <div key={r.id} className="card pad reveal" style={{ borderLeft: `3px solid ${STATUS_HUE[r.status]}` }}>
              <div className="row between wrap" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
                  <span className="badge accent mono" style={{ fontSize: 11 }}>{r.ref}</span>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{r.subject}</span>
                </span>
                <span className="row" style={{ gap: 6, flexShrink: 0 }}>
                  <span className="badge" style={{ fontSize: 9 }}>{r.status === "answered" ? "Answered" : r.status === "closed" ? "Closed" : "Open"}</span>
                  {r.status !== "closed" && <span className="badge" style={{ fontSize: 9 }}>Ball: {BALL_LABEL[r.ballInCourt]}</span>}
                </span>
              </div>
              <div style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.55 }}>{r.question}</div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                {[r.discipline, r.sheetCode && `Sheet ${r.sheetCode}`, `by ${r.raisedBy}`, relative(r.raisedOn), r.dueOn && `due ${fmtDate(r.dueOn)}`].filter(Boolean).join(" · ")}
              </div>
              {r.attachmentUrl && <a href={r.attachmentUrl} target="_blank" rel="noreferrer"><img src={r.attachmentUrl} alt="attachment" style={{ width: 120, height: 84, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)", marginTop: 8 }} /></a>}
              {r.answer && (
                <div className="panel" style={{ padding: "10px 12px", marginTop: 10, background: "color-mix(in srgb, var(--sky) 6%, var(--card))" }}>
                  <div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>Answer · {r.answeredBy}{r.answeredOn ? ` · ${fmtDate(r.answeredOn)}` : ""}</div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{r.answer}</div>
                </div>
              )}
              {canEdit && (
                <div className="row" style={{ gap: 8, marginTop: 12 }}>
                  <button className="btn accent sm" onClick={() => setAnswerFor(r)}><Icon.spark width={13} />{r.answer ? "Edit answer" : "Answer"}</button>
                  <button className="btn ghost sm" onClick={() => actions.setRfiStatus(p.id, r.id, r.status === "closed" ? "open" : "closed")}>{r.status === "closed" ? "Reopen" : "Close"}</button>
                  <span style={{ flex: 1 }} />
                  <button className="iconbtn" style={{ width: 30, height: 30 }} title="Remove" onClick={() => { if (confirm(`Remove ${r.ref}?`)) actions.removeRfi(p.id, r.id); }}><Icon.close width={13} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {raise && <RaiseRfiModal p={p} by={who} onClose={() => setRaise(false)} />}
      {answerFor && <AnswerRfiModal p={p} rfi={answerFor} by={who} onClose={() => setAnswerFor(null)} />}
    </Page>
  );
}

function RaiseRfiModal({ p, by, onClose }: { p: any; by: string; onClose: () => void }) {
  const [subject, setSubject] = useState("");
  const [question, setQuestion] = useState("");
  const [discipline, setDiscipline] = useState("Architecture");
  const [sheetCode, setSheetCode] = useState("");
  const [dueOn, setDueOn] = useState("");
  return (
    <Modal open onClose={onClose} title="Raise an RFI">
      <div className="col" style={{ gap: 14 }}>
        <div className="field"><label>Subject</label><input className="input" autoFocus value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Clarify slab edge detail at grid C" /></div>
        <div className="field"><label>Question</label><textarea className="input" rows={4} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Describe exactly what needs clarifying." /></div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Discipline</label>
            <select className="select" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>{["Architecture", "Structural", "MEP", "Interiors", "Civil", "Landscape", "Other"].map((d) => <option key={d}>{d}</option>)}</select>
          </div>
          <div className="field" style={{ width: 120 }}><label>Sheet</label><input className="input mono" value={sheetCode} onChange={(e) => setSheetCode(e.target.value)} placeholder="A-101" /></div>
          <div className="field" style={{ width: 150 }}><label>Reply by</label><input className="input" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} /></div>
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!subject.trim() || !question.trim()} onClick={() => { actions.createRfi(p.id, { subject, question, discipline, sheetCode, dueOn: dueOn || undefined, by, role: "studio" }); onClose(); }}><Icon.check width={15} />Raise RFI</button>
        </div>
      </div>
    </Modal>
  );
}

function AnswerRfiModal({ p, rfi, by, onClose }: { p: any; rfi: RFI; by: string; onClose: () => void }) {
  const [answer, setAnswer] = useState(rfi.answer ?? "");
  return (
    <Modal open onClose={onClose} title={`Answer ${rfi.ref}`}>
      <div className="col" style={{ gap: 14 }}>
        <div className="panel" style={{ padding: "10px 12px" }}><div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>{rfi.subject}</div><div style={{ fontSize: 13 }}>{rfi.question}</div></div>
        <div className="field"><label>Answer</label><textarea className="input" rows={5} autoFocus value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="The clarification / instruction." /></div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!answer.trim()} onClick={() => { actions.answerRfi(p.id, rfi.id, answer, by); onClose(); }}><Icon.check width={15} />Save answer</button>
        </div>
      </div>
    </Modal>
  );
}
